import {
  GameObject,
  Realm,
  System,
  vector2DToQuat,
  vector2IsZero,
  vector3Subtract,
} from "realms-engine";
import { TrackingCameraSystem } from "realms-engine-browser";
import {
  OtherPlayerGameObject,
  OtherPlayerGameObjectEventsMap,
} from "../gameObjects/player/otherPlayer.gameObject";
import {
  ChatMessage,
  ChatMessageSenderType,
  MMONetSchema,
  PlayerInput,
} from "mmo-common";
import {
  NetworkSlaveSystem,
  PhysicsPredictionSystem,
} from "realms-engine-network-client";
import { SelfPlayerGameObject } from "@game/gameObjects/player/selfPlayer.gameObject";
import { MMOCreatureSystem } from "./mmoCreatureSystem";

const SEND_INTERVAL_MS = 100;

export class PlayerSystem extends System<{
  healthChange: {
    health: number;
    maxHealth: number;
  };
  inCombatChange: OtherPlayerGameObjectEventsMap["inCombatChange"];
}> {
  systemName: string = "PlayerSystem";
  private playerHealth: number | null = null;
  private playerMaxHealth: number | null = null;
  private selfPlayerGameObject: SelfPlayerGameObject | null = null;
  private foundPlayer = false;

  private otherPlayerGameObjects = new Set<OtherPlayerGameObject>();
  private msBeforeSendPlayerState = 0;

  constructor(private characterId: number) {
    super([OtherPlayerGameObject, SelfPlayerGameObject]);
  }

  /**
   * Handle player input
   */
  handlePlayerInput(playerInput: PlayerInput) {
    if (!this.realm) {
      return;
    }

    // Handle movement input client side
    if (this.selfPlayerGameObject) {
      if (playerInput.move) {
        this.selfPlayerGameObject.move(playerInput.move);
      }
      if (playerInput.jump) {
        this.selfPlayerGameObject.jump();
      }
      if (playerInput.attack && !this.selfPlayerGameObject.isDead()) {
        // Autoaim
        const mmoCreatureSystem = this.realm.systems.get(MMOCreatureSystem);
        const closestHostileCreature =
          mmoCreatureSystem.getClosestHostileCreature(
            this.selfPlayerGameObject
          );
        if (closestHostileCreature) {
          const selfPosition = this.selfPlayerGameObject.getPosition();
          const direction = vector3Subtract(
            closestHostileCreature.getPosition(),
            selfPosition
          );
          if (!vector2IsZero(direction)) {
            const quaternion = vector2DToQuat({
              x: direction.x,
              y: direction.z,
            });
            playerInput.stateUpdate = {
              quaternion,
              position: selfPosition,
            };
            this.selfPlayerGameObject.setQuaternion(quaternion);
          }
        }

        playerInput.attack = this.selfPlayerGameObject.attack() ?? undefined;
      } else {
        playerInput.attack = undefined;
      }
    }

    // Send input to server
    this.realm.systems
      .get(NetworkSlaveSystem<MMONetSchema>)
      .getClient()
      .getConnection()
      .emit("input", playerInput);
  }

  /**
   * Gets the target character id
   */
  getSelfCharacterId() {
    return this.characterId;
  }

  /**
   * Handles a chat message
   */
  handleChatMessage(chatMessage: ChatMessage) {
    for (const player of Array.from(this.otherPlayerGameObjects.values())) {
      if (
        chatMessage.sender.type === ChatMessageSenderType.Player &&
        player.getCharacterId() === chatMessage.sender.characterId
      ) {
        player.getUI().showChatMessage(chatMessage);
        break;
      }
    }
  }

  onTargetGameObjectEnter(gameObject: GameObject<any>): void {
    if (gameObject instanceof OtherPlayerGameObject) {
      this.otherPlayerGameObjects.add(gameObject);
    }
  }

  onTargetGameObjectExit(gameObject: GameObject<any>): void {
    if (gameObject instanceof OtherPlayerGameObject) {
      this.otherPlayerGameObjects.delete(gameObject);
    }
  }

  /**
   * Gets the current players game object
   */
  getPlayerGameObject() {
    return this.selfPlayerGameObject;
  }

  processBeforeStep(delta: number, realm: Realm): void {
    this.foundPlayer = false;
  }

  step(delta: number, gameObject: GameObject, realm: Realm): void {
    this.msBeforeSendPlayerState = Math.max(
      this.msBeforeSendPlayerState - delta,
      0
    );

    if (gameObject instanceof SelfPlayerGameObject) {
      this.foundPlayer = true;
      this.selfPlayerGameObject = this.setSelfPlayerGameObject(gameObject);
      const physicsPredictionGameObject = realm.systems
        .get(PhysicsPredictionSystem)
        .getPhysicsPredictionGameObject(
          this.selfPlayerGameObject.getPhysicsGameObjectId()
        );
      if (physicsPredictionGameObject) {
        this.selfPlayerGameObject.setPhysicsPredictionGameObject(
          physicsPredictionGameObject
        );
      }

      realm.systems
        .get(TrackingCameraSystem)
        .setTarget(gameObject.getPosition())
        .setLookOffset({ x: 0, y: 10, z: 0 })
        .setCameraOffset({ x: 0, y: 10, z: 0 });

      // Emit health changed event
      const newPlayerHealth = gameObject.getHealth();
      const newPlayerMaxHealth = gameObject.getMaxHealth();
      if (
        this.playerHealth !== newPlayerHealth ||
        this.playerMaxHealth !== newPlayerMaxHealth
      ) {
        this.dispatch("healthChange", {
          health: newPlayerHealth,
          maxHealth: newPlayerMaxHealth,
        });
      }
      this.playerHealth = newPlayerHealth;
      this.playerMaxHealth = newPlayerMaxHealth;

      if (this.msBeforeSendPlayerState === 0 && this.realm) {
        this.msBeforeSendPlayerState = SEND_INTERVAL_MS;
        // Send input to server
        this.realm.systems
          .get(NetworkSlaveSystem<MMONetSchema>)
          .getClient()
          .getConnection()
          .emit("sendPlayerState", {
            creatureState: this.selfPlayerGameObject.getCurrentState(),
            position: this.selfPlayerGameObject.getPosition(),
            quaternion: this.selfPlayerGameObject.getQuaternion(),
          });
      }
    }
  }

  isCurrentlyInCombat(): boolean {
    return this.selfPlayerGameObject?.isInCombat() ?? false;
  }

  private setSelfPlayerGameObject(selfPlayerGameObject: SelfPlayerGameObject) {
    if (this.selfPlayerGameObject !== selfPlayerGameObject) {
      this.selfPlayerGameObject?.removeListener(
        "inCombatChange",
        this.onSelfInCombatChange
      );
      selfPlayerGameObject.addListener(
        "inCombatChange",
        this.onSelfInCombatChange
      );
    }
    this.selfPlayerGameObject = selfPlayerGameObject;
    return selfPlayerGameObject;
  }

  private onSelfInCombatChange = (
    e: OtherPlayerGameObjectEventsMap["inCombatChange"]
  ) => {
    this.dispatch("inCombatChange", e);
  };

  processAfterStep(delta: number, realm: Realm): void {
    if (!this.foundPlayer) {
      this.selfPlayerGameObject = null;
    }
  }
}
